/**
 * Curated Unsplash fallback images — single source of truth for all
 * fallback imagery used across the app (trip covers, activity photos, etc.).
 *
 * Each trip cover entry has a category, keywords for matching against trip
 * metadata, and a verified Unsplash CDN URL.
 */

const UNSPLASH_BASE = 'https://images.unsplash.com'
// `auto=format` lets the Unsplash CDN serve WebP/AVIF to browsers that
// advertise support — typically 30–50% smaller than JPEG, free LCP win.
const UNSPLASH_PARAMS_HD = '?w=1200&q=80&auto=format&fit=crop'
const UNSPLASH_PARAMS_SM = '?w=600&q=80&auto=format&fit=crop'
const UNSPLASH_SRCSET_WIDTHS = [400, 600, 800, 1200, 1600]

function unsplash(photoId: string, hd = true): string {
  return `${UNSPLASH_BASE}/${photoId}${hd ? UNSPLASH_PARAMS_HD : UNSPLASH_PARAMS_SM}`
}

/** The 'city' category cover. It is a recognisable Manhattan skyline (One World Trade Center). */
export const CITY_SKYLINE_COVER = unsplash('photo-1514565131-fce0801e5785')

/**
 * Build a responsive `srcSet` for an Unsplash CDN URL by rewriting the `w=`
 * query param across a fixed ladder of widths. Preserves `auto=format` so
 * each variant still negotiates WebP/AVIF. Returns `null` for non-Unsplash
 * URLs (e.g., Google Places photo proxies) so callers can fall back to a
 * single `src`.
 */
export function unsplashSrcSet(url: string): string | null {
  if (!url.startsWith(UNSPLASH_BASE)) return null
  const [base, qs = ''] = url.split('?')
  const params = new URLSearchParams(qs)
  if (!params.has('auto')) params.set('auto', 'format')
  if (!params.has('fit')) params.set('fit', 'crop')
  return UNSPLASH_SRCSET_WIDTHS
    .map((w) => {
      params.set('w', String(w))
      return `${base}?${params.toString()} ${w}w`
    })
    .join(', ')
}

// ── Activity-type fallbacks (used by ActivityPhoto component) ──────────────

export const ACTIVITY_FALLBACKS: Record<string, string> = {
  camp: unsplash('photo-1504280390367-361c6d9f38f4', false),
  class: unsplash('photo-1577896851231-70ef18881754', false),
  program: unsplash('photo-1503676260728-1c00da094a0b', false),
}
export const DEFAULT_ACTIVITY_FALLBACK = unsplash('photo-1469854523086-cc02fe5d8800', false)

interface FallbackImage {
  category: string
  url: string
  keywords: string[]
}

// ── Trip cover fallbacks (matched by destination/tags/description) ──────────

const TRIP_COVER_FALLBACKS: FallbackImage[] = [
  {
    category: 'beach',
    url: unsplash('photo-1507525428034-b723cf961d3e'),
    keywords: [
      'beach', 'tropical', 'coast', 'ocean', 'surf', 'shore', 'seaside',
      'caribbean', 'hawaii', 'cancun', 'maldives', 'bali', 'phuket',
      'bahamas', 'fiji', 'seychelles', 'aruba', 'turks', 'caicos',
      'tulum', 'playa', 'cabo', 'maui', 'oahu', 'kauai',
    ],
  },
  {
    category: 'mountains',
    url: unsplash('photo-1464822759023-fed622ff2c3b'),
    keywords: [
      'mountain', 'hiking', 'alps', 'alpine', 'trek', 'summit', 'peak',
      'climbing', 'trail', 'patagonia', 'nepal', 'switzerland', 'dolomites',
      'rocky mountains', 'rockies', 'andes', 'himalayas', 'denali',
      'yosemite', 'banff', 'glacier', 'altitude', 'everest',
    ],
  },
  {
    category: 'desert',
    url: unsplash('photo-1509316785289-025f5b846b35'),
    keywords: [
      'desert', 'sand', 'dune', 'sahara', 'arid', 'dry', 'canyon',
      'morocco', 'dubai', 'jordan', 'namibia', 'petra', 'monument valley',
      'sedona', 'death valley', 'mojave', 'utah', 'arizona', 'wadi',
      'marrakech', 'abu dhabi', 'oman',
    ],
  },
  {
    category: 'snow',
    url: unsplash('photo-1491002052546-bf38f186af56'),
    keywords: [
      'snow', 'winter', 'ski', 'skiing', 'snowboard', 'cold', 'frost',
      'arctic', 'lapland', 'chalet', 'lodge', 'aspen', 'whistler',
      'iceland', 'norway', 'finland', 'alaska', 'cozy', 'christmas',
      'colorado', 'tahoe', 'zermatt', 'chamonix', 'hokkaido',
    ],
  },
  {
    category: 'city',
    url: CITY_SKYLINE_COVER,
    keywords: [
      'city', 'urban', 'skyline', 'downtown', 'nightlife', 'metropolitan',
      'new york', 'nyc', 'manhattan', 'tokyo', 'london', 'dubai',
      'singapore', 'hong kong', 'chicago', 'las vegas', 'los angeles',
      'san francisco', 'miami', 'seattle', 'boston', 'shanghai',
      'skyscraper', 'museum', 'gallery', 'shopping',
    ],
  },
  {
    category: 'forest',
    url: unsplash('photo-1448375240586-882707db888b'),
    keywords: [
      'forest', 'woods', 'woodland', 'rainforest', 'jungle', 'trees',
      'nature', 'camping', 'cabin', 'pacific northwest', 'olympic',
      'redwood', 'sequoia', 'amazon', 'costa rica', 'borneo',
      'appalachian', 'smoky', 'smokey', 'great smoky',
    ],
  },
  {
    category: 'europe',
    url: unsplash('photo-1570077188670-e3a8d69ac5ff'),
    keywords: [
      'europe', 'mediterranean', 'santorini', 'greece', 'italy', 'spain',
      'portugal', 'france', 'paris', 'rome', 'barcelona', 'lisbon',
      'amsterdam', 'prague', 'vienna', 'budapest', 'dubrovnik', 'amalfi',
      'cinque terre', 'tuscany', 'provence', 'croatia', 'malta',
      'mykonos', 'florence', 'venice', 'berlin', 'munich', 'copenhagen',
    ],
  },
  {
    category: 'asia',
    url: unsplash('photo-1493976040374-85c8e12f0c0e'),
    keywords: [
      'asia', 'temple', 'pagoda', 'shrine', 'japan', 'kyoto',
      'southeast asia', 'cambodia', 'myanmar', 'vietnam', 'laos',
      'thailand', 'china', 'taiwan', 'korea', 'seoul', 'bangkok',
      'angkor', 'hanoi', 'siem reap', 'buddhist', 'zen',
    ],
  },
  {
    category: 'island',
    url: unsplash('photo-1514282401047-d79a71a590e8'),
    keywords: [
      'island', 'atoll', 'lagoon', 'overwater', 'bungalow', 'resort',
      'polynesia', 'tahiti', 'bora bora', 'zanzibar', 'galapagos',
      'mauritius', 'greek islands', 'azores', 'canary', 'madeira',
      'virgin islands', 'key west', 'bermuda',
    ],
  },
  {
    category: 'countryside',
    url: unsplash('photo-1523531294919-4bcd7c65e216'),
    keywords: [
      'countryside', 'rural', 'vineyard', 'wine', 'farm', 'rolling hills',
      'napa', 'agritourism', 'pastoral', 'cottage', 'village',
      'hill country', 'texas hill', 'willamette', 'sonoma', 'loire',
      'cotswolds', 'winery', 'lavender', 'sunflower',
    ],
  },
  {
    category: 'safari',
    url: unsplash('photo-1516426122078-c23e76319801'),
    keywords: [
      'safari', 'wildlife', 'africa', 'elephant', 'savanna', 'kenya',
      'tanzania', 'serengeti', 'kruger', 'game drive', 'namibia',
      'botswana', 'south africa', 'cape town', 'kilimanjaro', 'masai',
      'zanzibar', 'madagascar', 'gorilla', 'uganda', 'rwanda',
    ],
  },
  {
    category: 'lake',
    url: unsplash('photo-1439066615861-d1af74d74000'),
    keywords: [
      'lake', 'river', 'waterfront', 'reflection', 'calm', 'serene',
      'como', 'garda', 'tahoe', 'moraine', 'braies', 'bled',
      'fiord', 'fjord', 'waterfall', 'falls', 'niagara',
      'kayak', 'canoe', 'cruise', 'houseboat',
    ],
  },
]

const DEFAULT_TRAVEL_IMAGE = unsplash('photo-1500835556837-99ac94a94552')

interface CoverImageSubject {
  destination?: string
  title?: string
  description?: string
  tags?: string[]
  highlights?: string[]
}

function coverSearchText(trip: CoverImageSubject): string {
  return [
    trip.destination,
    trip.title,
    trip.description,
    ...(trip.tags ?? []),
    ...(trip.highlights ?? []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

/**
 * How many keyword hits a category needs before its photo may be captioned with
 * a real destination name.
 *
 * A single hit is routinely incidental: the weak keywords in these sets
 * ('nature', 'cozy', 'village', 'resort', 'cruise', 'shopping') fire on trips
 * that have nothing to do with the category, and the search text spans the
 * destination, title, description, tags AND highlights, so a genuine match
 * almost always produces several. Two independent hits is the line between
 * "this is a photo of the place" and "this is a photo of somewhere else".
 */
const CONFIDENT_COVER_MATCH_MIN_SCORE = 2

/**
 * Every category cover whose keyword score clears the confidence threshold,
 * best first. Ties break on declaration order, which is what keeps entry [0]
 * identical to `selectFallbackCoverImage`'s answer when there is one.
 *
 * `DEFAULT_TRAVEL_IMAGE` is deliberately NOT appended: it is the answer to
 * "we have no idea", which is exactly the case that must not be dressed up as
 * a photo of the destination.
 */
function rankConfidentCoverImages(trip: CoverImageSubject): string[] {
  const searchText = coverSearchText(trip)
  return TRIP_COVER_FALLBACKS
    .map((img, index) => ({
      url: img.url,
      index,
      score: img.keywords.reduce((n, kw) => (searchText.includes(kw) ? n + 1 : n), 0),
    }))
    .filter((entry) => entry.score >= CONFIDENT_COVER_MATCH_MIN_SCORE)
    .sort((a, b) => b.score - a.score || a.index - b.index)
    .map((entry) => entry.url)
}

/**
 * Covers for a SET of trips, as distinct as the keyword data allows — and
 * `undefined` wherever the data does not support a photo at all.
 *
 * Selecting per trip is wrong for a set: three options generated from ONE brief
 * are usually three variations on one region, so `selectFallbackCoverImage`
 * hands back the same category photo two or three times — three identical
 * postcards side by side, which reads as broken rather than curated. Each trip
 * here claims its best still-unclaimed CONFIDENT match.
 *
 * The `undefined` is the important half of the contract. The caller
 * (GuestPlanner's postcards) captions each photo with the option's real
 * destination, so a keyword-guessed frame is a claim about a specific place —
 * "Kraków, Poland" over a Caribbean beach is worse than no photograph, and the
 * generic travel image under a real city name is worse still. Callers must
 * treat `undefined` as "render the warm gradient ground", never as "substitute
 * something".
 *
 * Order matters: earlier trips get first pick.
 */
export function selectDistinctFallbackCoverImages(
  trips: CoverImageSubject[],
): (string | undefined)[] {
  const taken = new Set<string>()
  return trips.map((trip) => {
    const chosen = rankConfidentCoverImages(trip).find((url) => !taken.has(url))
    if (chosen) taken.add(chosen)
    return chosen
  })
}

/**
 * Select a contextually appropriate fallback cover image based on trip metadata.
 * Matches destination, title, description, tags, and highlights against keyword sets.
 */
export function selectFallbackCoverImage(trip: CoverImageSubject): string {
  const searchText = coverSearchText(trip)

  let bestMatch: FallbackImage | null = null
  let bestScore = 0

  for (const img of TRIP_COVER_FALLBACKS) {
    let score = 0
    for (const kw of img.keywords) {
      if (searchText.includes(kw)) score++
    }
    if (score > bestScore) {
      bestScore = score
      bestMatch = img
    }
  }

  return bestMatch ? bestMatch.url : DEFAULT_TRAVEL_IMAGE
}
